// WebSocket signaling frames — docs/02-signaling-and-api.md §4.
// Every frame is a JSON envelope `{ type, seq, payload }`. `seq` is a
// per-connection monotonic number assigned by the server for server→client
// frames; client→server frames set it to 0 (the client has no seq to assign).

import { z } from 'zod';
import {
  CallId,
  ConnId,
  EpochMillis,
  Ed25519Signature,
  Sdp,
  Seq,
  Token,
  Uuid,
} from './primitives.js';
import { ApiError, FriendRequest, PresenceStatus } from './models.js';

/** `call.decline` reasons. */
export const CallDeclineReason = z.enum(['declined', 'busy']);
export type CallDeclineReason = z.infer<typeof CallDeclineReason>;

/**
 * `call.end` reasons. `crossed` cancels one half of a crossed-call (glare) pair;
 * `answered_elsewhere` stops ringing on a callee's other sessions.
 */
export const CallEndReason = z.enum([
  'hangup',
  'timeout',
  'error',
  'answered_elsewhere',
  'crossed',
]);
export type CallEndReason = z.infer<typeof CallEndReason>;

// ---------------------------------------------------------------------------
// Payload schemas (one per frame type)
// ---------------------------------------------------------------------------

/** `sys.auth` (C→S) — must be the client's first frame, within 5 s. */
export const SysAuthPayload = z
  .object({
    token: Token,
    /** connId to resume a flapped connection. */
    resume: ConnId.optional(),
    /** Last server seq the client processed; server replays anything newer. */
    lastSeq: Seq.optional(),
    /**
     * Who holds this socket: the app itself (default), or the Android native
     * stay-reachable listener (`reach`) — which keeps the device ringable but
     * does NOT mean the user is in the app. Presence maps app → 'app',
     * reach-only → 'reachable'.
     */
    agent: z.enum(['app', 'reach']).optional(),
  })
  .strict();
export type SysAuthPayload = z.infer<typeof SysAuthPayload>;

/** `sys.hello` (S→C) — first server frame after successful auth. */
export const SysHelloPayload = z
  .object({
    connId: ConnId,
    resumed: z.boolean(),
    serverTime: EpochMillis,
  })
  .strict();
export type SysHelloPayload = z.infer<typeof SysHelloPayload>;

/** `sys.ping` / `sys.pong` / `sys.revoked` carry no fields. */
export const EmptyPayload = z.object({}).strict();
export type EmptyPayload = z.infer<typeof EmptyPayload>;

/** `presence.update` (S→C) — for friends only. */
export const PresenceUpdatePayload = z
  .object({
    userId: Uuid,
    /** Back-compat boolean: true ⇔ `status === 'app'`. */
    online: z.boolean(),
    status: PresenceStatus.optional(),
  })
  .strict();
export type PresenceUpdatePayload = z.infer<typeof PresenceUpdatePayload>;

/** `friend.request` / `friend.accepted` (S→C) — real-time REST mirror. */
export const FriendEventPayload = z
  .object({
    request: FriendRequest,
  })
  .strict();
export type FriendEventPayload = z.infer<typeof FriendEventPayload>;

/** `call.invite` (both). */
export const CallInvitePayload = z
  .object({
    callId: CallId,
    from: Uuid,
    to: Uuid,
    sdp: Sdp,
    fingerprintSig: Ed25519Signature,
    callerKeyFingerprint: z.string().min(1),
    /**
     * Server-side ring deadline (epoch ms), when known. Optional and additive:
     * a late-delivered invite rings only the remaining window (docs/02 §4;
     * TODO.md #4). The caller never sets it — the server owns the ring timer —
     * and the authoritative copy travels in `call.sync`; this field only lets a
     * consumer honour a deadline when one is present.
     */
    expiresAt: EpochMillis.optional(),
  })
  .strict();
export type CallInvitePayload = z.infer<typeof CallInvitePayload>;

/** `call.ringing` (both). */
export const CallRingingPayload = z.object({ callId: CallId }).strict();
export type CallRingingPayload = z.infer<typeof CallRingingPayload>;

/** `call.accept` (both) — answer SDP. */
export const CallAcceptPayload = z
  .object({
    callId: CallId,
    sdp: Sdp,
    fingerprintSig: Ed25519Signature,
  })
  .strict();
export type CallAcceptPayload = z.infer<typeof CallAcceptPayload>;

/** `call.decline` (both). */
export const CallDeclinePayload = z
  .object({
    callId: CallId,
    reason: CallDeclineReason,
  })
  .strict();
export type CallDeclinePayload = z.infer<typeof CallDeclinePayload>;

/** A single trickle-ICE candidate (opaque SDP fragment + routing metadata). */
export const IceCandidate = z
  .object({
    candidate: z.string(),
    sdpMid: z.string().nullable().optional(),
    sdpMLineIndex: z.number().int().nonnegative().nullable().optional(),
    usernameFragment: z.string().nullable().optional(),
  })
  .strict();
export type IceCandidate = z.infer<typeof IceCandidate>;

/** `call.ice` (both) — trickle ICE, batched ≤50 ms. */
export const CallIcePayload = z
  .object({
    callId: CallId,
    candidates: z.array(IceCandidate),
  })
  .strict();
export type CallIcePayload = z.infer<typeof CallIcePayload>;

/** `call.renegotiate` (both) — ICE restarts / mid-call renegotiation. */
export const CallRenegotiatePayload = z
  .object({
    callId: CallId,
    sdp: Sdp,
    fingerprintSig: Ed25519Signature,
  })
  .strict();
export type CallRenegotiatePayload = z.infer<typeof CallRenegotiatePayload>;

/** `call.end` (both) — courtesy teardown / stop-ringing. */
export const CallEndPayload = z
  .object({
    callId: CallId,
    reason: CallEndReason,
  })
  .strict();
export type CallEndPayload = z.infer<typeof CallEndPayload>;

/** Role of THIS session's user in the `call.sync` snapshot. */
export const CallSyncRole = z.enum(['caller', 'callee']);
export type CallSyncRole = z.infer<typeof CallSyncRole>;

/** Server-authoritative lifecycle phase of the snapshotted call. */
export const CallSyncPhase = z.enum(['ringing', 'answered']);
export type CallSyncPhase = z.infer<typeof CallSyncPhase>;

/** The current call in a `call.sync` snapshot (null-able at the payload level). */
export const CallSyncCall = z
  .object({
    callId: CallId,
    role: CallSyncRole,
    peerId: Uuid,
    phase: CallSyncPhase,
    /** Ring deadline (epoch ms); present only while `phase === 'ringing'`. */
    expiresAt: EpochMillis.optional(),
    /**
     * The verbatim `call.invite` payload, present only for
     * `role === 'callee' && phase === 'ringing'` so a session that never
     * received the invite (killed app, resume-failed reconnect) can still ring
     * or upgrade its generic push ring to the caller's name.
     */
    invite: CallInvitePayload.optional(),
  })
  .strict();
export type CallSyncCall = z.infer<typeof CallSyncCall>;

/**
 * `call.sync` (S→C) — the authoritative current call state for this session's
 * user, sent after `sys.hello` on EVERY authenticated connect (fresh, resumed,
 * and resume-failed alike). Client reconciliation is idempotent, so re-sending
 * it redundantly is safe; it closes the gap where a `call.end`/`call.accept`
 * queued during an outage is lost when resume fails (TODO.md #4, docs/02 §4).
 */
export const CallSyncPayload = z
  .object({
    /** null ⇒ this user has no tracked call. */
    call: CallSyncCall.nullable(),
    /** Server clock (epoch ms), for skew estimation. */
    serverTime: EpochMillis,
  })
  .strict();
export type CallSyncPayload = z.infer<typeof CallSyncPayload>;

// ---------------------------------------------------------------------------
// Framed messages (envelope + typed payload), and the discriminated union
// ---------------------------------------------------------------------------

const frame = <T extends string, P extends z.ZodType>(type: T, payload: P) =>
  z
    .object({
      type: z.literal(type),
      seq: Seq,
      payload,
    })
    .strict();

/**
 * `sys.rpc` (C→S) — run a REST call over the socket that is already open.
 *
 * This is a TRANSPORT, not a second API: `method` + `path` name an existing
 * route and the server dispatches to the very same handler, with the identity
 * the socket already proved at `sys.auth`. Nothing here may grow request
 * semantics of its own, or the two surfaces will drift and only one of them
 * will be contract-tested.
 *
 * Why it exists (docs/04 §1): Russian carriers enforce at flow SETUP and freeze
 * foreign-datacenter TLS after ~15-20KB, and some police the number of
 * simultaneous connections to one host. A request that rides an established
 * socket pays no new handshake, opens no new connection, and carries a few
 * hundred bytes — it survives all three of those where a fresh HTTPS request
 * dies. In the field incident this socket stayed up for minutes while every
 * REST call failed.
 */
export const SysRpcPayload = z
  .object({
    /** Correlates the reply; unique per connection, chosen by the client. */
    id: z.string().min(1).max(64),
    method: z.enum(['GET', 'POST', 'PATCH', 'PUT', 'DELETE']),
    /** Route beneath the API version prefix, e.g. `/me`. Never a full URL:
     *  the socket already determines which server this reaches, and accepting
     *  an absolute URL here would turn the server into an open relay. */
    path: z.string().regex(/^\/[^\s?#]*(\?[^\s#]*)?$/, 'must be an absolute route path'),
    /** JSON request body; absent for bodyless methods. */
    body: z.unknown().optional(),
  })
  .strict();

/** `sys.rpc.result` (S→C) — the tunnelled response. `status` is the HTTP status
 *  the same route would have returned, so callers keep one error vocabulary. */
export const SysRpcResultPayload = z
  .object({
    id: z.string().min(1).max(64),
    status: z.number().int().min(100).max(599),
    body: z.unknown().optional(),
  })
  .strict();

/**
 * `sys.reauth` (C→S) — replace the access token a live connection is acting on.
 *
 * Needed because `sys.rpc` runs each tunnelled request through the real auth
 * middleware using the credential the socket presented at `sys.auth`. Access
 * tokens last 15 minutes; signaling itself does not care, because the server
 * authorized the connection once — but a tunnelled REST call is authorized per
 * request, so without this every socket older than its token starts answering
 * 401 to `/me`, `/friends`, everything, until it happens to flap. That would
 * hit every user, not only blocked ones.
 *
 * A second `sys.auth` cannot serve: it means "open/resume a connection" and the
 * server closes 4400 on a repeat, which is a close-code contract clients
 * implement. This frame only swaps the credential on a connection that is
 * already authenticated, and the server must verify the new token names the
 * SAME user and session — it is a renewal, never a way to become someone else
 * on an established socket.
 */
export const SysReauthPayload = z.object({ token: Token }).strict();

export const SysAuthFrame = frame('sys.auth', SysAuthPayload);
export const SysReauthFrame = frame('sys.reauth', SysReauthPayload);
export const SysRpcFrame = frame('sys.rpc', SysRpcPayload);
export const SysRpcResultFrame = frame('sys.rpc.result', SysRpcResultPayload);
export const SysHelloFrame = frame('sys.hello', SysHelloPayload);
export const SysPingFrame = frame('sys.ping', EmptyPayload);
export const SysPongFrame = frame('sys.pong', EmptyPayload);
export const SysRevokedFrame = frame('sys.revoked', EmptyPayload);
export const PresenceUpdateFrame = frame('presence.update', PresenceUpdatePayload);
export const FriendRequestFrame = frame('friend.request', FriendEventPayload);
export const FriendAcceptedFrame = frame('friend.accepted', FriendEventPayload);
export const CallInviteFrame = frame('call.invite', CallInvitePayload);
export const CallRingingFrame = frame('call.ringing', CallRingingPayload);
export const CallAcceptFrame = frame('call.accept', CallAcceptPayload);
export const CallDeclineFrame = frame('call.decline', CallDeclinePayload);
export const CallIceFrame = frame('call.ice', CallIcePayload);
export const CallRenegotiateFrame = frame('call.renegotiate', CallRenegotiatePayload);
export const CallEndFrame = frame('call.end', CallEndPayload);
export const CallSyncFrame = frame('call.sync', CallSyncPayload);
export const ErrorFrame = frame('error', ApiError);

/** The full set of WS frames, discriminated on `type`. */
export const WsFrame = z.discriminatedUnion('type', [
  SysAuthFrame,
  SysReauthFrame,
  SysRpcFrame,
  SysRpcResultFrame,
  SysHelloFrame,
  SysPingFrame,
  SysPongFrame,
  SysRevokedFrame,
  PresenceUpdateFrame,
  FriendRequestFrame,
  FriendAcceptedFrame,
  CallInviteFrame,
  CallRingingFrame,
  CallAcceptFrame,
  CallDeclineFrame,
  CallIceFrame,
  CallRenegotiateFrame,
  CallEndFrame,
  CallSyncFrame,
  ErrorFrame,
]);
export type WsFrame = z.infer<typeof WsFrame>;

/** All WS frame `type` string literals. */
export const WS_FRAME_TYPES = [
  'sys.auth',
  'sys.reauth',
  'sys.rpc',
  'sys.rpc.result',
  'sys.hello',
  'sys.ping',
  'sys.pong',
  'sys.revoked',
  'presence.update',
  'friend.request',
  'friend.accepted',
  'call.invite',
  'call.ringing',
  'call.accept',
  'call.decline',
  'call.ice',
  'call.renegotiate',
  'call.end',
  'call.sync',
  'error',
] as const;
export type WsFrameType = (typeof WS_FRAME_TYPES)[number];
